import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { savedListingApi } from '@/api/savedListingApi';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { getApiErrorMessage } from '@/utils';

export const useSavedListings = (params: { entityType?: string; page?: number; limit?: number; orderBy?: string; orderDirection?: string } = { page: 1, limit: 10 }) => {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ['saved-listings', user?.id, params],
    queryFn: () => savedListingApi.getSavedListings(params),
    enabled: !!user,
  });
};

export const useToggleSaveListing = () => {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();
  const { user } = useAuthStore();
  // Fetch up to 100 items to check isSaved state reliably (limit=1000 was throwing an error)
  const { data: savedListingsResult } = useSavedListings({});

  const isSaved = (entityId: string) => {
    return savedListingsResult?.data?.some((listing: any) => listing.entityId === entityId) ?? false;
  };

  const saveMutation = useMutation({
    mutationFn: ({ entityId, entityType = 'JobOffer' }: { entityId: string; entityType?: string }) =>
      savedListingApi.saveListing(entityId, entityType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-listings', user?.id] });
      addToast('success', 'Đã lưu tin thành công');
    },
    onError: (err: any) => {
      addToast('error', getApiErrorMessage(err, 'Có lỗi xảy ra khi lưu tin'));
    },
  });

  const unsaveMutation = useMutation({
    mutationFn: ({ entityId, entityType = 'JobOffer' }: { entityId: string; entityType?: string }) =>
      savedListingApi.unsaveListing(entityId, entityType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-listings', user?.id] });
      addToast('success', 'Đã bỏ lưu tin');
    },
    onError: (err: any) => {
      addToast('error', getApiErrorMessage(err, 'Có lỗi xảy ra khi bỏ lưu tin'));
    },
  });

  const toggleSave = (entityId: string, entityType = 'JobOffer') => {
    if (!user) {
      addToast('error', 'Vui lòng đăng nhập để lưu tin');
      return;
    }

    if (isSaved(entityId)) {
      unsaveMutation.mutate({ entityId, entityType });
    } else {
      saveMutation.mutate({ entityId, entityType });
    }
  };

  return {
    isSaved,
    toggleSave,
    isPending: saveMutation.isPending || unsaveMutation.isPending,
  };
};
